import { Link } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { BasketItem } from "../../types/types";

interface MiniBasketDropdownProps {
  lang: string;
  cartItems: BasketItem[];
  onClose: () => void;
}

const getFeatureAttributes = (
  selectedFeature: BasketItem["selectedFeature"]
): { name: string; value: string }[] => {
  if (!selectedFeature) return [];
  const feature = Array.isArray(selectedFeature)
    ? selectedFeature[0]
    : selectedFeature;
  return feature?.attributes || [];
};

const formatFeatureAttributes = (
  attributes: { name: string; value: string }[]
): string =>
  attributes
    .map(
      (attr) =>
        `${attr.name.charAt(0).toUpperCase() + attr.name.slice(1)}: ${attr.value}`
    )
    .join(", ");

const formatPrice = (price: number) =>
  new Intl.NumberFormat("uz-UZ", {
    style: "currency",
    currency: "UZS",
  }).format(price);

const MiniBasketDropdown: React.FC<MiniBasketDropdownProps> = ({
  lang,
  cartItems,
  onClose,
}) => {
  const { t } = useTranslation();
  const total = cartItems.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );

  return (
    <div className="flex max-h-[80vh] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl sm:w-96">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h3 className="font-semibold text-gray-900">{t("basket.title")}</h3>
        <button
          onClick={onClose}
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="close"
        >
          <X size={18} />
        </button>
      </div>

      {cartItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
          <ShoppingBag className="h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-500">{t("basket.emptyTitle")}</p>
        </div>
      ) : (
        <>
          <ul className="flex-1 divide-y divide-gray-100 overflow-y-auto">
            {cartItems.map((item) => {
              const attributes = getFeatureAttributes(item.selectedFeature);
              const image = Array.isArray(item.product?.image)
                ? item.product.image[0]
                : item.product?.image;

              return (
                <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <img
                    src={image || "https://placehold.co/600x400/EEE/31343C"}
                    alt={item.product?.title}
                    className="h-12 w-12 shrink-0 rounded-lg border border-gray-100 bg-gray-50 object-contain"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {item.product?.title}
                    </p>
                    {attributes.length > 0 && (
                      <p className="truncate text-xs text-gray-500">
                        {formatFeatureAttributes(attributes)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {item.quantity} x {formatPrice(item.price || 0)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="border-t border-gray-100 px-4 py-3.5">
            <div className="mb-3 flex justify-between text-sm">
              <span className="text-gray-500">{t("basket.totalLabel")}</span>
              <span className="font-semibold text-gray-900">
                {formatPrice(total)}
              </span>
            </div>
            <Link
              to={`/${lang}/basket`}
              onClick={onClose}
              className="flex w-full items-center justify-center rounded-xl bg-brand-gradient py-2.5 font-medium text-white transition hover:opacity-90"
            >
              {t("basket.viewBasket")}
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default MiniBasketDropdown;
